/**
 * DateRangeField — single date-range selector for the campaign form's
 * "Schedule & status" section (Chelsie Aug-14: "Date Range Selection" — one
 * calendar instead of two separate Start/End date inputs).
 *
 * Controlled component: `from`/`to` are the existing yyyy-MM-dd form-state
 * strings (both independently optional — start-only / end-only / none remain
 * representable); every calendar interaction propagates immediately through
 * `onChange(from, to)`. The calendar itself is react-day-picker in range mode
 * with `resetOnSelect`: the first click sets the start (from-only), the second
 * completes the range in whichever direction it falls (an earlier second click
 * shifts the start back), and the next click after a complete range starts a
 * new one — every branch emits an ordered pair, so end >= start holds by
 * construction.
 *
 * The popover is deliberately minimal and hand-rolled — same overlay approach
 * as the rest of this SPA (no radix, no general popover primitive) — but the
 * panel itself renders through a React portal into `document.body`,
 * positioned `fixed` from the trigger's `getBoundingClientRect()`.
 *
 * Presentation switches at the canonical mobile boundary
 * (`useMobileViewport`, <768 — src/lib/breakpoints.ts):
 *
 * - Desktop keeps the anchored popover: re-measured on open, window resize
 *   and capture-phase scroll (follows the trigger when the page scrolls;
 *   opens upward when there's no room below), with the left edge clamped so
 *   the panel never runs past the right side of the viewport (task 064-05
 *   D2, clamp added in 105-04).
 * - Mobile (<768) anchors the panel to the field the same way: top edge 4px
 *   under the trigger, left edge at the field's left, one month plus the
 *   built-in month navigation (the second month is reached through the nav
 *   buttons, not by overflow). The height is clamped to the viewport space
 *   under the anchor (innerHeight − top − 2×margin), so the bottom date rows
 *   stay inside the panel's box instead of being cut by the screen edge
 *   (#159); overflow-y remains only as a supplement on very short screens.
 *   The width hugs the content (max-content, maxWidth clamped to the right
 *   viewport edge). Both surfaces follow the field on scroll through the
 *   same resize/capture-scroll listeners — ruling chain: 105-04 #157 placed
 *   the mobile panel once on open with no scroll coupling; QA round 3
 *   verdict #3 (2026-10-20, #159) reverses that mobile half — the panel
 *   must re-anchor on scroll — while keeping the other half: dismissal is
 *   still tap-only, a scroll gesture never closes the panel.
 *
 * Outside dismissal is tap-based, not raw pointerdown: a pointerdown is
 * recorded and the popover only closes on pointerup if the pointer stayed
 * within a small slop (a tap) and landed outside the trigger root and the
 * portalled panel. A touch scroll gesture starts with pointerdown outside
 * but moves — it is not a tap, so scrolling the page no longer dismisses
 * the calendar (105-04 #157). The trigger root *and* the portalled panel
 * both count as "inside" (D3), since the panel is no longer a DOM
 * descendant of the root once portalled.
 */

import * as React from 'react';
import { format } from 'date-fns';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import type { DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import { cn } from '../lib/utils';
import { useMobileViewport } from '../lib/breakpoints';

interface DateRangeFieldProps {
  /** Inclusive start, yyyy-MM-dd ('' = open). */
  from: string;
  /** Inclusive end, yyyy-MM-dd ('' = open). */
  to: string;
  /** Propagates (from, to) after each calendar interaction; '' empties a side. */
  onChange: (from: string, to: string) => void;
}

// yyyy-MM-dd ↔ Date via LOCAL midnight. Never `new Date('yyyy-MM-dd')` here —
// that parses as UTC midnight and shifts the day in non-UTC environments. The
// UTC helpers in campaign-form.tsx own the wire format; this is display only.
const parseLocalDate = (s: string): Date | undefined => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const formatLocalDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const formatDisplayDate = (date: Date): string => format(date, 'MMM d, yyyy');

// Both presentations anchor to the trigger: top edge 4px below the trigger
// (above with more room there on desktop), left-clamped into the viewport.
// Mobile clamps the panel height to the viewport space under the anchor so
// the bottom date rows stay inside the screen (#159); desktop opens upward
// through `bottom` when there isn't room below.
type PanelPosition =
  | { left: number; top: number; maxHeight: number }
  | { left: number; top: number }
  | { left: number; bottom: number };

// Viewport margin for the panel clamps: the mobile height/width limits and
// the desktop left-clamp.
const VIEWPORT_MARGIN = 8;
// Pointer movement (px, Manhattan) below which a down→up pair counts as a tap
// rather than a scroll/drag gesture.
const TAP_SLOP = 10;

// Mobile placement: anchored to the trigger — top edge 4px under it, left
// edge at the field's left — and clamped to the viewport space under the
// anchor so no date row is hidden past the screen edge (#159). overflow-y
// supplements the clamp on very short screens; the inline-style branch
// clamps the max-content width to the right viewport edge.
const mobileAnchoredPosition = (rect: DOMRect): PanelPosition => {
  const top = rect.bottom + 4;
  return {
    left: rect.left,
    top,
    maxHeight: window.innerHeight - top - 2 * VIEWPORT_MARGIN,
  };
};

export function DateRangeField({ from, to, onChange }: DateRangeFieldProps) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<PanelPosition>({ left: 0, top: 0 });
  // The in-progress calendar selection; re-synced from the form state each
  // time the popover opens. No other durable state of its own.
  const [draft, setDraft] = React.useState({ from, to });

  // Canonical mobile boundary (<768) — the only switch for the mobile
  // presentation (conventions §Responsive layout; 105-04).
  const isMobileViewport = useMobileViewport();

  // Re-measure the trigger (and, once mounted, the panel's own box) to place
  // the desktop popover — downward by default, upward when there isn't room
  // below, left-clamped so the panel stays inside the viewport. On mobile
  // the panel anchors 4px under the trigger with its height clamped to the
  // viewport space that remains (#159); the layout effect below re-measures
  // after mount.
  const measure = React.useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    if (isMobileViewport) {
      setPosition(mobileAnchoredPosition(rect));
      return;
    }
    const panel = panelRef.current;
    const panelHeight = panel?.offsetHeight ?? 0;
    const panelWidth = panel?.offsetWidth ?? 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < panelHeight && spaceAbove > spaceBelow;
    // Keep the panel's left edge inside [margin, viewport − panelWidth −
    // margin]; before the panel mounts (panelWidth 0) this is a no-op beyond
    // the left floor, and the layout effect below re-measures after mount.
    const left = Math.min(
      Math.max(rect.left, VIEWPORT_MARGIN),
      Math.max(VIEWPORT_MARGIN, window.innerWidth - panelWidth - VIEWPORT_MARGIN)
    );
    setPosition(
      openUpward
        ? { left, bottom: window.innerHeight - rect.top + 4 }
        : { left, top: rect.bottom + 4 }
    );
  }, [isMobileViewport]);

  React.useLayoutEffect(() => {
    if (open) measure();
  }, [open, isMobileViewport, measure]);

  // Both surfaces: follow the trigger on window resize and (capture-phase)
  // scroll — the app scrolls inside an overflow container, so the listener
  // lives on document with capture. Dismissal stays tap-only: a scroll
  // gesture moves past the tap slop, so following never closes the panel.
  React.useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', measure);
    document.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      document.removeEventListener('scroll', measure, true);
    };
  }, [open, measure]);

  // Close on outside tap and Escape while open. "Tap" = pointerdown followed
  // by a pointerup within TAP_SLOP — a touch scroll gesture moves further and
  // does NOT dismiss (#157); pointercancel (the browser hijacking the pointer
  // for scrolling) just drops the pending down. "Inside" means the trigger
  // root or the portalled panel.
  React.useEffect(() => {
    if (!open) return;
    let down: { x: number; y: number; pointerId: number; target: EventTarget | null } | undefined;
    const handlePointerDown = (e: PointerEvent) => {
      down = { x: e.clientX, y: e.clientY, pointerId: e.pointerId, target: e.target };
    };
    const handlePointerUp = (e: PointerEvent) => {
      const start = down;
      down = undefined;
      if (!start || e.pointerId !== start.pointerId) return;
      const moved = Math.abs(e.clientX - start.x) + Math.abs(e.clientY - start.y) >= TAP_SLOP;
      if (moved) return; // scroll/drag gesture, not a dismiss tap
      if (!(start.target instanceof Node)) return;
      const insideRoot = rootRef.current?.contains(start.target) ?? false;
      const insidePanel = panelRef.current?.contains(start.target) ?? false;
      if (!insideRoot && !insidePanel) setOpen(false);
    };
    const handlePointerCancel = () => {
      down = undefined;
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerCancel);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerCancel);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const openPopover = () => {
    setDraft({ from, to });
    if (isMobileViewport) {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setPosition(mobileAnchoredPosition(rect));
    } else {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setPosition({ left: rect.left, top: rect.bottom + 4 });
    }
    setOpen(true);
  };

  const fromDate = parseLocalDate(from);
  const toDate = parseLocalDate(to);

  // "MMM D, YYYY – MMM D, YYYY", open-ended for partial values, placeholder
  // when both sides are empty.
  const display =
    fromDate && toDate
      ? `${formatDisplayDate(fromDate)} – ${formatDisplayDate(toDate)}`
      : fromDate
        ? `${formatDisplayDate(fromDate)} – …`
        : toDate
          ? `… – ${formatDisplayDate(toDate)}`
          : 'Select dates';
  const isPlaceholder = !fromDate && !toDate;

  const selected: DateRange | undefined =
    draft.from || draft.to
      ? { from: parseLocalDate(draft.from), to: parseLocalDate(draft.to) }
      : undefined;

  const handleSelect = (range: DateRange | undefined) => {
    const next = {
      from: range?.from ? formatLocalDate(range.from) : '',
      to: range?.to ? formatLocalDate(range.to) : '',
    };
    setDraft(next);
    onChange(next.from, next.to);
    if (next.from && next.to) setOpen(false); // complete range picked → dismiss
  };

  const handleClear = () => {
    setDraft({ from: '', to: '' });
    onChange('', '');
  };

  const draftFrom = parseLocalDate(draft.from);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Start and end date"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openPopover())}
        className={cn(
          'flex h-10 w-full items-center gap-2 rounded-lg bg-surface px-3 py-2 text-left text-sm outline-none transition',
          'hover:bg-line/40 focus:shadow-[inset_0_0_0_2px_var(--color-accent)]',
          isPlaceholder ? 'text-faint' : 'text-ink'
        )}
      >
        <span className="min-w-0 flex-1 truncate">{display}</span>
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          className={cn('shrink-0 transition-transform', open ? 'rotate-180' : '')}
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Choose date range"
            style={
              'maxHeight' in position
                ? {
                    position: 'fixed',
                    left: position.left,
                    width: 'max-content',
                    maxWidth: window.innerWidth - position.left - VIEWPORT_MARGIN,
                    top: position.top,
                    maxHeight: position.maxHeight,
                    overflowY: 'auto',
                  }
                : {
                    position: 'fixed',
                    left: position.left,
                    ...('top' in position ? { top: position.top } : { bottom: position.bottom }),
                  }
            }
            className="z-[60] rounded-lg bg-surface p-2 text-ink hairline shadow-lg"
            // The Field label wrapping this control (when used inside one) is
            // no longer a DOM ancestor of this portalled panel, so it can no
            // longer forward a click on a non-interactive panel area (weekday
            // headers, gutters) to the trigger — but this guard is cheap
            // insurance and the day/Clear buttons keep their own handlers.
            onClick={(e) => e.preventDefault()}
          >
            <DayPicker
              mode="range"
              numberOfMonths={isMobileViewport ? 1 : 2}
              resetOnSelect
              selected={selected}
              onSelect={handleSelect}
              defaultMonth={
                draftFrom ? new Date(draftFrom.getFullYear(), draftFrom.getMonth(), 1) : undefined
              }
            />
            <div className="mt-1 flex justify-end border-t border-line pt-1">
              <button
                type="button"
                onClick={handleClear}
                className="rounded-md px-2 py-1 text-xs font-semibold text-accent transition hover:bg-paper hover:text-ink"
              >
                Clear
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
